import apiClient from './client';

export interface WorkflowStep {
  id: string;
  stepOrder: number;
  stepType: 'WARNING' | 'DIAGNOSTIC' | 'TREATMENT' | 'MONITORING' | 'PREVENTION';
  title: string;
  description: string;
  actions: string[];
  requiredEquipment?: string[];
  estimatedDuration?: number;
  completed: boolean;
  completedAt?: Date;
  notes?: string;
}

export interface WorkflowTrigger {
  id: string;
  triggerType: 'symptom' | 'test_result' | 'environmental' | 'schedule';
  condition: any;
  action: string;
  priority: string;
  triggered: boolean;
  triggeredAt?: Date;
}

export interface DiseaseWorkflow {
  id: string;
  birdId: string;
  diseaseName: string;
  diseaseCategory: string;
  severityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  mortalityRate: 'LOW' | 'MEDIUM' | 'HIGH';
  contagious: boolean;
  zoonotic?: boolean;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PAUSED' | 'FAILED';
  startedAt: string;
  completedAt?: string;
  workflowSteps: WorkflowStep[];
  triggers: WorkflowTrigger[];
  earlyWarningSigns?: {
    symptoms: string[];
    thresholds: any;
    monitoringFrequency: string;
  };
  diagnosticProtocol?: {
    tests: string[];
    procedures: string[];
    timeline: string;
  };
  treatmentWorkflow?: {
    medications: string[];
    procedures: string[];
    timeline: string;
    followUp: string[];
    supportiveCare?: string[];
    phases?: Array<{
      phase: string;
      duration: string;
      goals: string[];
      actions: string[];
    }>;
    contraindications?: string[];
    nutritionSupport?: string[];
    environmentalManagement?: string[];
    durationGuidance?: string;
    speciesNotes?: string[];
    protocolVersion?: number;
  };
  monitoringSchedule?: {
    frequency: string;
    parameters: string[];
    alerts: string[];
  };
  preventionMeasures: string[];
  notes?: string;
  recommendations: string[];
  actionItems: string[];
  assignedTo?: string;
  progressPercentage: number;
  nextStepDue?: string;
  createdAt: string;
  updatedAt: string;
  bird?: {
    id: string;
    bandId?: string;
    species?: { commonName?: string };
    healthStatus?: string;
    cageNumber?: string;
  };
}

export interface WorkflowDetailBird {
  id: string;
  bandId?: string;
  species?: string;
  healthStatus?: string;
  cageNumber?: string;
}

export interface WorkflowDetailTreatment {
  id: string;
  treatmentName: string;
  medicationName?: string;
  dosage: string;
  frequency: string;
  treatmentStatus: string;
  startDate: string;
  endDate?: string;
  administrationRoute: string;
}

export interface WorkflowDetail {
  workflow: DiseaseWorkflow;
  bird: WorkflowDetailBird;
  currentStep: WorkflowStep | null;
  symptomTriggers: string[];
  treatments: {
    active: WorkflowDetailTreatment[];
    all: WorkflowDetailTreatment[];
    count: number;
  };
  diseaseCatalog: {
    id: string;
    name: string;
    treatments?: string;
    diagnosis?: string;
    prevention?: string;
    medicines: string[];
  } | null;
  timeline: Array<WorkflowStep & { isCurrent: boolean }>;
}

export interface CreateDiseaseWorkflowDto {
  birdId: string;
  diseaseName: string;
  diseaseCategory: string;
  severityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  mortalityRate: 'LOW' | 'MEDIUM' | 'HIGH';
  contagious: boolean;
  zoonotic?: boolean;
  startedAt: string;
  workflowSteps: WorkflowStep[];
  triggers?: WorkflowTrigger[];
  notes?: string;
}

export interface UpdateDiseaseWorkflowDto {
  workflowSteps?: WorkflowStep[];
  status?: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PAUSED' | 'FAILED';
  notes?: string;
  assignedTo?: string;
}

export interface WorkflowStatistics {
  total: number;
  active: number;
  completed: number;
  cancelled: number;
  overdue: number;
  byDisease: { [key: string]: number };
  bySeverity: { [key: string]: number };
  averageCompletionTime: number;
}

export const diseaseWorkflowsApi = {
  // Create a new disease workflow
  create: async (data: CreateDiseaseWorkflowDto): Promise<DiseaseWorkflow> => {
    const response = await apiClient.post<DiseaseWorkflow>('/disease-workflows', data);
    return response.data;
  },

  // Get all disease workflows
  getAll: async (params?: {
    birdId?: string;
    diseaseName?: string;
    status?: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PAUSED' | 'FAILED';
  }): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows', params);
    return response.data;
  },

  // Get a specific disease workflow
  getById: async (id: string): Promise<DiseaseWorkflow> => {
    const response = await apiClient.get<DiseaseWorkflow>(`/disease-workflows/${id}`);
    return response.data;
  },

  // Get enriched workflow detail (pathway, treatments, bird)
  getDetail: async (id: string): Promise<WorkflowDetail> => {
    const response = await apiClient.get<WorkflowDetail>(`/disease-workflows/${id}/detail`);
    return response.data;
  },

  // Update a disease workflow
  update: async (id: string, data: UpdateDiseaseWorkflowDto): Promise<DiseaseWorkflow> => {
    const response = await apiClient.patch<DiseaseWorkflow>(`/disease-workflows/${id}`, data);
    return response.data;
  },

  // Delete a disease workflow
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/disease-workflows/${id}`);
  },

  // Trigger a disease workflow based on symptoms
  triggerWorkflow: async (birdId: string, diseaseName: string, symptoms: string[]): Promise<DiseaseWorkflow> => {
    const response = await apiClient.post<DiseaseWorkflow>('/disease-workflows/trigger', {
      birdId,
      diseaseName,
      symptoms
    });
    return response.data;
  },

  // Complete a specific workflow step
  completeStep: async (id: string, stepId: string, notes?: string): Promise<DiseaseWorkflow> => {
    const response = await apiClient.patch<DiseaseWorkflow>(`/disease-workflows/${id}/complete-step/${stepId}`, {
      notes
    });
    return response.data;
  },

  // Get active workflows
  getActiveWorkflows: async (): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows/active/list');
    return response.data;
  },

  // Get overdue workflows
  getOverdueWorkflows: async (): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows/overdue/list');
    return response.data;
  },

  // Get workflow statistics
  getWorkflowStatistics: async (): Promise<WorkflowStatistics> => {
    const response = await apiClient.get<WorkflowStatistics>('/disease-workflows/statistics/summary');
    return response.data;
  },

  // Get workflows by bird
  getByBird: async (birdId: string): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows', { birdId });
    return response.data;
  },

  // Get workflows by disease
  getByDisease: async (diseaseName: string): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows', { diseaseName });
    return response.data;
  },

  // Get workflows by status
  getByStatus: async (status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PAUSED' | 'FAILED'): Promise<DiseaseWorkflow[]> => {
    const response = await apiClient.get<DiseaseWorkflow[]>('/disease-workflows', { status });
    return response.data;
  },

  // Complete a workflow
  complete: async (id: string, notes?: string): Promise<DiseaseWorkflow> => {
    return diseaseWorkflowsApi.update(id, {
      status: 'COMPLETED',
      notes
    });
  },

  // Cancel a workflow
  cancel: async (id: string, notes?: string): Promise<DiseaseWorkflow> => {
    return diseaseWorkflowsApi.update(id, {
      status: 'CANCELLED',
      notes
    });
  },

  // Pause a workflow
  pause: async (id: string, notes?: string): Promise<DiseaseWorkflow> => {
    return diseaseWorkflowsApi.update(id, {
      status: 'PAUSED',
      notes
    });
  },

  // Resume a workflow
  resume: async (id: string): Promise<DiseaseWorkflow> => {
    return diseaseWorkflowsApi.update(id, {
      status: 'ACTIVE'
    });
  }
};
